import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LayoutGrid, X } from 'lucide-react';
import type { AppView, Project } from '@/types';
import { getLatestFloorplanSession, FLOORPLAN_STEP_LABEL, FLOORPLAN_FLOW_VIEWS } from '@/lib/floorplanSession';

interface FloorplanResumeBubbleProps {
  projects: Project[];
  currentView: AppView;
  currentProjectId: string | null;
  onResume: (projectId: string) => void;
}

/**
 * Schwebe-Bubble für den Grundriss-Flow (Pendant zur WizardResumeBubble): zeigt
 * den zuletzt nicht abgeschlossenen Grundriss-Durchlauf und setzt ihn per Klick
 * fort. Blendet sich aus, wenn man ohnehin gerade im Flow (oder im Workspace
 * desselben Projekts, dort gibt es das Inline-Banner) ist. Sitzt über der
 * Staging-Bubble, damit beide bei Bedarf gestapelt sichtbar sind.
 */
export const FloorplanResumeBubble: React.FC<FloorplanResumeBubbleProps> = ({
  projects,
  currentView,
  currentProjectId,
  onResume,
}) => {
  const [dismissedSig, setDismissedSig] = useState<string | null>(null);

  const latest = getLatestFloorplanSession();
  if (!latest) return null;
  if ((FLOORPLAN_FLOW_VIEWS as readonly string[]).includes(currentView)) return null;
  if (currentView === 'workspace' && currentProjectId === latest.projectId) return null;

  const project = projects.find((p) => p.id === latest.projectId);
  if (!project) return null;

  const sig = `${latest.projectId}:${latest.updatedAt}`;
  if (dismissedSig === sig) return null;

  return (
    <div className="fixed bottom-40 right-6 z-40 flex justify-end">
      <AnimatePresence>
        <motion.div
          key={sig}
          initial={{ opacity: 0, y: 8, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.97 }}
          transition={{ duration: 0.18, ease: 'easeOut' }}
          className="flex max-w-[280px] items-center gap-2.5 rounded-2xl border border-primary/30 bg-card px-3 py-2.5 shadow-xl"
        >
          <button
            type="button"
            onClick={() => onResume(latest.projectId)}
            className="flex min-w-0 flex-1 items-center gap-2.5 text-left"
            title="Grundrisserstellung fortsetzen"
          >
            <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary/10">
              <LayoutGrid className="h-5 w-5 text-primary" />
            </span>
            <span className="min-w-0">
              <span className="block text-xs font-semibold text-foreground">Grundriss fortsetzen</span>
              <span className="block truncate text-[11px] text-muted-foreground">
                {project.name} · {FLOORPLAN_STEP_LABEL[latest.view]}
              </span>
            </span>
          </button>
          <button
            type="button"
            onClick={() => setDismissedSig(sig)}
            className="flex-shrink-0 text-muted-foreground transition-colors hover:text-foreground"
            aria-label="Ausblenden"
          >
            <X className="h-4 w-4" />
          </button>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};
